import React from 'react';
import { midiToName } from '../../services/warmupStepPlan';
import { ACCOMPANIMENT_OPTIONS, GUIDE_LEVELS, METER_OPTIONS, VOICE_OPTIONS, phaseLabel, voiceOptionLabel } from './warmupFormat';

/**
 * The deck — the key readout and every transport control.
 *
 * Split out of WarmupPlayer on 2026-09-04 (Lesson Dashboard I1 phase 0) with
 * its markup unchanged, so that I1 can lift it into the sticky conducting strip
 * under the lesson bar while the routine stays in the right column. On the
 * Warmups page and in the lesson today it still renders exactly where it did.
 *
 * Takes the whole `useWarmupRoutinePlayer` result as one prop, the way
 * LessonWarmups already takes `warmups` — the alternative is a dozen props
 * threaded through two call sites for no gain.
 *
 * EVERY CONTROL IS ALWAYS VISIBLE (CLAUDE.md): no hover-only affordances, and
 * every button is at least 44px. Disabled state is a real `disabled`, but note
 * these are plain buttons, not `wa-*` — no boolean-prop trap here.
 */
/**
 * `sections` says which parts to render, and `className` names the wrapper.
 * I1 phase 3 needs this: below 1200 the strip carries the readout and the main
 * transport while the key/tempo/guide rows move down into the routine column,
 * so the SAME component renders twice over ONE engine. Two renders are fine —
 * the invariant that matters is one `useWarmupRoutinePlayer`, not one JSX
 * instance — but they must not both call themselves `.wp__deck`, or "exactly
 * one deck on the page" stops meaning anything.
 */
// `keyLabel` (pass 3 B4): the conducting strip names its key "Warm-up key",
// visibly and as the key group's accessible name, so "Key" never labels both
// the warm-up key and a track's Key control on one page. Other hosts pass
// nothing and render exactly as before.
const WarmupConsole = ({ wp, sections = ['readout', 'transport', 'keys', 'mix'], className = 'wp__deck', keyLabel = null }) => {
	const { currentEx, state, items, current, routineDone, engine, isActive, playLabel, loading, onPlayPause, playItem } = wp;
	const has = (k) => sections.includes(k);

	return (
		<div className={className}>
			{has('readout') && (
			<div className="wp__readout" aria-live="polite">
				<div className="wp__now">{currentEx ? currentEx.name : 'Nothing selected'}</div>
				{keyLabel && <div className="wp__keylabel">{keyLabel}</div>}
				<div className="wp__key">
					<span className="wp__keynow">{state.tonicName || (items[current] ? midiToName(items[current].startTonic) : '—')}</span>
					<span className="wp__keytop">planned top {state.topName || (items[current] ? midiToName(items[current].topTonic) : '—')}</span>
				</div>
				<div className="wp__phase">
					{phaseLabel(state)}
					{state.octaveShift ? ` · ${state.octaveShift < 0 ? 'an octave down' : 'an octave up'}` : ''}
					{routineDone ? 'Routine finished' : ''}
				</div>
			</div>
			)}

			{has('transport') && (
			<div className="wp__transport wp__transport--main" role="group" aria-label="Transport">
				<button type="button" className="wp__btn" title="Rewind this exercise" onClick={() => engine.rewind()} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-backward-fast" aria-hidden="true" />
					<span>Rewind</span>
				</button>
				<button type="button" className="wp__btn" title="Back a key" onClick={() => engine.back()} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-backward-step" aria-hidden="true" />
					<span>Back</span>
				</button>
				<button type="button" className="wp__btn wp__btn--main" title={playLabel} onClick={onPlayPause} disabled={loading || !items.length}>
					<i className={`fa-sharp fa-solid fa-${state.status === 'playing' ? 'pause' : 'play'}`} aria-hidden="true" />
					<span>{playLabel}</span>
				</button>
				<button
					type="button"
					className="wp__btn"
					title={state.phase === 'hold' ? 'One more semitone up' : 'Next key'}
					onClick={() => engine.next()}
					disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-forward-step" aria-hidden="true" />
					<span>{state.phase === 'hold' ? 'One more' : 'Next'}</span>
				</button>
				<button
					type="button"
					className="wp__btn"
					title="Next exercise"
					onClick={() => current + 1 < items.length && playItem(current + 1, { countIn: false })}
					disabled={current + 1 >= items.length}>
					<i className="fa-sharp fa-solid fa-forward-fast" aria-hidden="true" />
					<span>Exercise</span>
				</button>
			</div>
			)}

			{has('keys') && (
			<div className="wp__transport wp__transport--keys" role="group" aria-label={keyLabel || 'Key'}>
				<button type="button" className="wp__btn" title="Transpose down a semitone" onClick={() => engine.transpose(-1)} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-minus" aria-hidden="true" />
					<span>Semitone</span>
				</button>
				<button type="button" className="wp__btn" title="Transpose up a semitone" onClick={() => engine.transpose(1)} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
					<span>Semitone</span>
				</button>
				<button
					type="button"
					className="wp__btn"
					title={state.octaveShift < 0 ? 'Back up the octave' : 'Drop an octave'}
					onClick={() => (state.octaveShift < 0 ? engine.raiseOctave() : engine.dropOctave())}
					disabled={!isActive}>
					<i className={`fa-sharp fa-solid fa-arrow-${state.octaveShift < 0 ? 'up' : 'down'}`} aria-hidden="true" />
					<span>{state.octaveShift < 0 ? 'Octave up' : 'Drop octave'}</span>
				</button>
				<button type="button" className="wp__btn" title="Stop climbing and come down" onClick={() => engine.descend()} disabled={!isActive || state.phase === 'down'}>
					<i className="fa-sharp fa-solid fa-arrow-turn-down" aria-hidden="true" />
					<span>Descend</span>
				</button>
			</div>
			)}

			{has('mix') && (
			<div className="wp__transport wp__transport--mix" role="group" aria-label="Tempo and guide">
				<button type="button" className="wp__btn" title="Slower" onClick={() => engine.setTempo(state.bpm - 4)} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-minus" aria-hidden="true" />
					<span>Tempo</span>
				</button>
				<span className="wp__bpm">{state.bpm} bpm</span>
				<button type="button" className="wp__btn" title="Faster" onClick={() => engine.setTempo(state.bpm + 4)} disabled={!isActive}>
					<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
					<span>Tempo</span>
				</button>
				<span className="wp__seg" role="group" aria-label="Guide level">
					{GUIDE_LEVELS.map(([value, label]) => (
						<button
							key={value}
							type="button"
							className={`wp__segbtn${state.guideLevel === value ? ' is-on' : ''}`}
							aria-pressed={state.guideLevel === value}
							onClick={() => engine.setGuideLevel(value)}>
							{label}
						</button>
					))}
				</span>
				<span className="wp__seg" role="group" aria-label="Guide voice">
					{VOICE_OPTIONS.map(([value, label]) => (
						<button
							key={value}
							type="button"
							className={`wp__segbtn${state.voice === value ? ' is-on' : ''}`}
							aria-pressed={state.voice === value}
							title={
								value === 'voice' && state.voiceAvailable === false
									? 'The sustained set is not in this build'
									: value === 'auto'
										? 'Slides and long holds on the sustained set, everything else on the piano'
										: value === 'voice'
											? 'Guide line on the sustained set for every exercise'
											: 'Guide line on the piano for every exercise'
							}
							disabled={value === 'voice' && state.voiceAvailable === false}
							onClick={() => engine.setVoice(value)}>
							{voiceOptionLabel(state, value, label)}
						</button>
					))}
				</span>
				<span className="wp__seg" role="group" aria-label="Accompaniment">
					{ACCOMPANIMENT_OPTIONS.map(([value, label]) => (
						<button
							key={value}
							type="button"
							className={`wp__segbtn${state.accompaniment === value ? ' is-on' : ''}`}
							aria-pressed={state.accompaniment === value}
							title={value === 'full' ? 'Walking bass, a pulse on the 5th, and the next key cued by its dominant' : 'One chord per rep with a V7 turnaround'}
							onClick={() => engine.setAccompaniment(value)}>
							{label}
						</button>
					))}
				</span>
				<span className="wp__seg" role="group" aria-label="Time signature">
					{METER_OPTIONS.map(([value, label]) => (
						<button
							key={value}
							type="button"
							className={`wp__segbtn${state.meter === value ? ' is-on' : ''}`}
							aria-pressed={state.meter === value}
							title={`${label} time`}
							onClick={() => engine.setMeter(value)}>
							{label}
						</button>
					))}
				</span>
			</div>
			)}
		</div>
	);
};

export default WarmupConsole;
